import {useState, useEffect} from 'react'
import {useParams, useNavigate} from 'react-router'
import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import SkeletonCards from '@/customComponents/editRideComponents/SkeletonCards.jsx'
import DestinationSection from '@/customComponents/editRideComponents/PageModals/DestinationSection.jsx'
import DateTimeSection from '@/customComponents/editRideComponents/PageModals/DateTimeSection.jsx'
import SmartPickupFlow from '@/customComponents/sharedComponents/smartTime/SmartPickupFlow.jsx'
import CarSection from '@/customComponents/editRideComponents/PageModals/CarSection.jsx'
import PassengerSection from '@/customComponents/editRideComponents/PageModals/PassengerSection.jsx'
import LuggageSection from '@/customComponents/editRideComponents/PageModals/LuggageSection.jsx'
import SpecialRequestPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/SpecialRequestPopup.jsx'
import CancelRide from '@/customComponents/uiComponents/global/CancelRide.jsx'
import FlightDelayedPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/FlightDelayedPopup.jsx'
import LostBaggagePopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/LostBaggagePopup.jsx'
import AirportPickupPerksPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/AirportPickupPerksPopup.jsx'
import ComingSoonPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/ComingSoonPopup.jsx'
import PayAfterDropoffPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PayAfterDropoffPopup.jsx'
import SuccessPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/SuccessPopup.jsx'
import PoliciesPopup from '@/customComponents/editRideComponents/PageModals/ComponentPopups/PoliciesPopup.jsx'
import RouteCard from '@/customComponents/editRideComponents/RouteCard.jsx'
import DateTimeCard from '@/customComponents/editRideComponents/DateTimeCard.jsx'
import {PassengersCard, ChildSeatsCard, LuggageItemsCard, OversizedCard} from '@/customComponents/editRideComponents/SummaryCards.jsx'
import CarDetailsCard from '@/customComponents/editRideComponents/CarDetailsCard.jsx'
import SpecialRequestCard from '@/customComponents/editRideComponents/SpecialRequestCard.jsx'
import PaymentMethodsCard from '@/customComponents/editRideComponents/PaymentMethodsCard.jsx'
import InfoButtons from '@/customComponents/editRideComponents/InfoButtons.jsx'
import AlertMessage from '@/customComponents/homeComponents/AlertMessage.jsx'
import ExclusivePerksBar from '@/customComponents/sharedComponents/ExclusivePerksBar.jsx'
import RideBackButton from '@/customComponents/sharedComponents/RideBackButton.jsx'
import AirportPerksModal from '@/customComponents/sharedComponents/AirportPerksModal.jsx'
import useEditTrip from '@/hooks/useEditTrip'
import useGateNudge from '@/hooks/useGateNudge'

export default function RideEdit() {
    const {rideId} = useParams()
    const navigate = useNavigate()
    const e = useEditTrip(rideId)
    const isReturnFlow = e.trip?.trip_type === 'ReturnBooking'
    const [showPoliciesPopup, setShowPoliciesPopup] = useState(false)
    const [showPerksModal, setShowPerksModal] = useState(false)
    // "Change limit" warning: shown on the FIRST submit click, then dismissed for the
    // rest of this page-load. In-memory only — a reload resets it and shows it again.
    const [showChangeWarning, setShowChangeWarning] = useState(false)
    const [changeWarnSeen, setChangeWarnSeen] = useState(false)
    // Mobile: submitting with a date / flight / time missing scrolls to that field and says
    // why under it (same as PhoneBookingConfirmation). The no-field blocks (modification
    // limit, nothing editable) keep the disabled button + tooltip everywhere.
    const {isMobile: nudgeMobile, nudging, errRef: nudgeErrRef, trigger: triggerNudge, activeFor: nudgeFor} = useGateNudge()
    const handleSubmitClick = () => {
        if (gateMessage) {
            if (gateField && nudgeMobile) triggerNudge({section: 'datetime', field: gateField, message: gateMessage})
            return
        }
        if (!changeWarnSeen) {
            setShowChangeWarning(true)
            setChangeWarnSeen(true)
            return
        }
        setShowChangeWarning(false)
        e.doUpdate()
    }
    const isLocked = ['Completed', 'Cancelled', 'NoShow'].includes(e.trip?.status)
    const lockedLabel = e.trip?.status_label || e.trip?.status

    useEffect(() => {
        document.body.classList.add('ride-edit-page')
        return () => document.body.classList.remove('ride-edit-page')
    }, [])

    const renderSection = () => {
        switch (e.editingSection) {
            case 'destination': return <DestinationSection mode={e.destinationPart || 'all'} destinations={e.destinations} committedDestinations={e.committedDestinations} setDestinations={e.handleDestinationsChange} onDone={() => e.handleSectionDone('destination')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError}/>
            case 'car': return <CarSection availableCars={e.availableCars} selectedCar={e.selectedCar} committedCarTypeId={e.committedCarTypeId} setSelectedCar={(car) => { e.setSelectedCar(car) }} onDone={() => e.handleSectionDone('car')} onClose={e.closeEdit} isLoading={e.isRecalculating} isRecalculating={e.isRecalculating} error={e.sectionSaveError} driverTip={e.driverTip} setDriverTip={e.setDriverTip} onTipChange={e.handleTipChange} onCouponRecalculate={e.handleCouponRecalculate} breakdownCarId={e.carBreakdownId} setBreakdownCarId={e.setCarBreakdownId} couponPolicy={e.getFieldPolicy('discount_code')}/>
            case 'passengers': return <PassengerSection tripId={rideId} passengers={e.passengers} committedPassengers={e.committedPassengers} setPassengers={e.handlePassengersChange} childSeats={e.childSeats} committedChildSeats={e.committedChildSeats} setChildSeats={e.handleChildSeatsChange} passengerTypes={e.passengerTypes} childSeatTypes={e.childSeatTypes} onDone={() => e.handleSectionDone('passengers')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError} view={e.passengerView} onViewChange={e.setPassengerView} only={e.passengerOnly} passengerTypesPolicy={e.getFieldPolicy('passenger_types')} childSeatsPolicy={e.getFieldPolicy('child_seats')}/>
            case 'luggage': return <LuggageSection tripId={rideId} luggage={e.luggage} committedLuggage={e.committedLuggage} setLuggage={e.handleLuggageChange} oversizedLuggage={e.oversizedLuggage} committedOversizedLuggage={e.committedOversizedLuggage} setOversizedLuggage={e.handleOversizedChange} luggageTypes={e.luggageTypes} onDone={() => e.handleSectionDone('luggage')} onClose={e.closeEdit} isLoading={e.isRecalculating} error={e.sectionSaveError} oversizedMode={e.luggageOversizedMode} setOversizedMode={e.setLuggageOversizedMode}/>
            default: return null
        }
    }

    if (e.isLoading) {
        return <div className="py-5 pb-10 max-md:bg-(--bg-grey)"><SkeletonCards/></div>
    }

    if (!e.trip) {
        return <div className="py-5 pb-10"><div className="max-w-3xl mx-auto text-center p-4"><p className="text-(--text-secondary)">Trip not found</p></div></div>
    }

    // Submit gate — the FIRST missing required field blocks submit. `gateMessage` is the tooltip
    // under the button; `gateField` drives the SAME field's pulse animation in DateTimeCard so the
    // pulse and the tooltip stay in sync. (Date/time fall back to trip.scheduled_at to avoid a
    // disabled flash before DateTimeCard's mount-commit lands. atLimit is a global block, no field.)
    const gateUsed = Number(e.trip?.edit_policy?.modifications_used) || 0
    const gateTotal = Number(e.trip?.edit_policy?.free_modifications) || 0
    const atLimit = gateUsed >= gateTotal && gateTotal > 0
    // Nothing is editable at all — the band this trip falls in opens no fields. Every
    // section already renders disabled (getFieldPolicy/getSectionPolicy return canEdit:false
    // for each field), so submitting could only ever be a no-op: block it like atLimit does.
    // Guarded on edit_policy existing, since a missing policy means "unrestricted".
    const editPolicy = e.trip?.edit_policy
    const nothingEditable = !!editPolicy && (editPolicy.can_edit === false || !(editPolicy.editable_fields || []).length)
    const hasDate = !!e.liveDatetime?.date || !!e.trip?.scheduled_at
    const hasTime = !!e.liveDatetime?.time || !!e.trip?.scheduled_at
    const gateField = atLimit || nothingEditable ? null
        : !hasDate ? 'date'
        : (e.isFromAirport && !e.hasFlightNumber) ? 'flight'
        : !hasTime ? 'time'
        : null
    const gateMessage = atLimit ? 'There is no modification available now'
        : nothingEditable ? "You can't edit the booking"
        : !hasDate ? 'Please select the arrival date to continue'
        : (e.isFromAirport && !e.hasFlightNumber) ? 'Please enter your flight number to continue'
        : !hasTime ? 'Please select the arrival time to continue'
        : null
    // A field gate on a phone is a nudge, not a block — see handleSubmitClick.
    const nudgeable = !!gateField && nudgeMobile
    const nudgeError = nudgeFor(gateMessage)

    return (
        <div className="py-5 pb-10 max-md:bg-(--bg-grey)">
            {/* Back sits at the left edge of the content column, vertically centred on
                the title block (which stays centred — hence absolute, not a flex row). */}
            <div className="max-w-3xl mx-auto text-center mb-5 relative px-14">
                <RideBackButton className="absolute left-2 top-1/2 -translate-y-1/2"/>
                <h1 className="font-bold text-2xl">{isReturnFlow ? 'Edit Return Booking' : 'Edit Booking'}</h1>
                {(() => {
                    const used = Number(e.trip?.edit_policy?.modifications_used) || 0
                    const total = Number(e.trip?.edit_policy?.free_modifications) || 0
                    const atLimit = used >= total && total > 0
                    return <p className={`mb-0! text-sm ${atLimit ? 'text-red-500' : 'text-(--text-secondary)'}`}>{used} of {total} free modifications used</p>
                })()}
            </div>

            <div className="max-w-3xl mx-auto mb-5 flex flex-col gap-7">
                <div className="prebook-compare">
                    <ExclusivePerksBar onClick={() => setShowPerksModal(true)}/>
                </div>
            </div>

            <div className="relative max-w-3xl mx-auto flex flex-col gap-4 p-5 pb-8 bg-(--bg-grey) rounded-(--radius-2xl) max-md:p-0 max-md:rounded-none max-md:bg-transparent">
                <AlertMessage paymentHold={e.trip.payment_hold}/>
                {isLocked && (
                    <div className="absolute inset-0 z-20 bg-white/70 backdrop-blur-sm rounded-(--radius-2xl) flex justify-center p-5" style={{paddingTop: '50%'}}>
                        <div className="bg-white rounded-(--radius-2xl) shadow-lg px-4 py-6 max-w-md text-center h-fit flex flex-col gap-3 items-center">
                            <h6 className="mb-0!">This trip is {lockedLabel} you can't modify this trip</h6>
                            {e.trip?.status === 'Cancelled' && e.trip?.cancelled_by && (
                                <span className="bg-(--bg-grey) px-4 py-2 rounded-full">Cancelled by {e.trip.cancelled_by}</span>
                            )}
                            {e.trip?.status === 'Cancelled' && e.trip?.cancellation_reason && (
                                <span><b>Reason:</b> {e.trip.cancellation_reason}</span>
                            )}
                            <a href={import.meta.env.VITE_BOOKING_PORTAL_URL} className="underline text-(--text-primary) font-medium">Create New Booking</a>
                        </div>
                    </div>
                )}

                <RouteCard title={e.isFromAirport ? 'From Airport' : 'Start Ride Address'} summary="origin" showBanner={false} includeStopovers={e.isToAirport}
                           trip={{...e.trip, origin: e.committedDestinations.origin, destination: e.committedDestinations.destination, stop_overs: e.committedDestinations.stopOvers}}
                           expanded={e.expanded.has('route')} onToggle={() => e.toggle('route')} onEdit={() => e.openEdit('destination', {part: e.isToAirport ? 'originStops' : 'origin'})} policy={e.getFieldPolicy('origin')}/>

                <RouteCard title="Drop off Address" summary="destination" showBanner={false} includeStopovers={!e.isToAirport}
                           trip={{...e.trip, origin: e.committedDestinations.origin, destination: e.committedDestinations.destination, stop_overs: e.committedDestinations.stopOvers}}
                           expanded={e.expanded.has('dropoff')} onToggle={() => e.toggle('dropoff')} onEdit={() => e.openEdit('destination', {part: e.isToAirport ? 'destinationOnly' : 'dropoff'})} policy={e.getSectionPolicy('dropoff')}/>

                <DateTimeCard
                    trip={e.trip} parentScheduledAt={e.trip?.parent_trip?.scheduled_at}
                    isAirportTrip={e.isAirportTrip}
                    isFromAirport={e.isFromAirport} isToAirport={e.isToAirport}
                    flightNumber={e.sharedFlightNumber}
                    onFlightNumberChange={e.handleFlightNumberChange}
                    onFlightClear={e.clearFlight} flightAdded={e.hasFlightNumber}
                    onSmartTime={() => e.setSmartTimePopup(true)}
                    applySmartTimeRef={e.applySmartTimeRef}
                    onDateTimeChange={e.setLiveDatetime} onDateChange={e.handleDateChange} onTimeChange={e.handleTimeChange}
                    dateHint={e.dateHint} setDateHint={(v) => { e.setDateHint(v); if (!v && e.isFromAirport) e.setFlightHint(true) }}
                    hidePulse={true}
                    pulseField={gateField}
                    gateError={nudgeError?.field ? nudgeError : null} gateErrorRef={nudgeErrRef}
                    scheduledAtPolicy={e.getFieldPolicy('scheduled_at')}
                    flightPolicy={e.getFieldPolicy('flight_number')}
                    dateTimeSectionRef={e.dateTimeSectionRef}
                    leadTimeError={e.minLeadTimeError}
                />

                <PassengersCard trip={e.trip} expanded={e.expanded.has('passengers')} onToggle={() => e.toggle('passengers')} onEdit={() => e.openEdit('passengers', {passengerOnly: 'passengers'})} policy={e.getFieldPolicy('passenger_types')}/>

                <ChildSeatsCard trip={e.trip} expanded={e.expanded.has('childseats')} onToggle={() => e.toggle('childseats')} onEdit={() => e.openEdit('passengers', {passengerOnly: 'childseats'})} policy={e.getFieldPolicy('child_seats')}/>

                <LuggageItemsCard trip={e.trip} expanded={e.expanded.has('luggage')} onToggle={() => e.toggle('luggage')} onEdit={() => e.openEdit('luggage')} policy={e.getSectionPolicy('luggage')}/>

                <OversizedCard trip={e.trip} expanded={e.expanded.has('oversized')} onToggle={() => e.toggle('oversized')} onEdit={() => e.openEdit('luggage', {oversized: true})} policy={e.getSectionPolicy('luggage')}/>

                <div className="flex gap-4 items-stretch max-md:flex-col">
                    <SpecialRequestCard trip={e.trip} specialRequest={e.specialRequest} contact={e.contact} onEditSpecialRequest={() => e.setShowSRPopup(true)} specialRequestPolicy={e.getSectionPolicy('special_request')}/>
                    <CarDetailsCard trip={e.trip} driverTip={e.driverTip} setDriverTip={e.setDriverTip} onTipChange={e.handleTipChange} isRecalculating={e.isRecalculating} onEditCar={() => e.openEdit('car')} carPolicy={e.getSectionPolicy('car')} gratuityPolicy={e.getFieldPolicy('gratuity_percentage')}/>
                </div>

                <PaymentMethodsCard trip={e.trip} savedCards={e.savedCards} selectedCardId={e.selectedCardId} setSelectedCardId={e.setSelectedCardId} onCardAdded={(c) => { e.setSavedCards(prev => [...prev, c]); e.setSelectedCardId(c.id) }} walletAmountCents={e.trip?.wallet_amount_cents} selectedOnly={true}/>

                <InfoButtons onInfoPopup={e.setInfoPopup}/>

                {e.bookingError && <p className="text-red-500 text-center text-sm">{e.bookingError}</p>}

                <div className="relative">
                    <button type="button" onClick={handleSubmitClick}
                            disabled={e.isSubmitting || (!!gateMessage && !nudgeable) || !!e.minLeadTimeError}
                            className={`hopn-add-passenger-button ${nudging ? 'opacity-60 pointer-events-none' : ''}`}>
                        Pay after drop-off — Continue
                    </button>
                    {gateMessage && (
                        <div className={`${gateField ? 'max-md:hidden ' : ''}absolute top-full right-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-10 max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:right-7.5`}>
                            <p className="mb-0!">{gateMessage}</p>
                        </div>
                    )}
                    {showChangeWarning && (() => {
                        const availableChanges = Math.max(0, gateTotal - gateUsed)
                        return (
                            <div className="absolute top-full right-0 mt-3 bg-[#3c393c] text-white rounded-xl p-3 text-center text-sm z-20 max-w-[280px] after:content-[''] after:absolute after:w-0 after:h-0 after:border-l-24 after:border-l-transparent after:border-r-[9px] after:border-r-transparent after:border-b-20 after:border-b-[#3c393c] after:-top-4.75 after:right-7.5">
                                <div className="w-full flex justify-end mb-1">
                                    <button type="button" onClick={() => setShowChangeWarning(false)} aria-label="Dismiss"
                                            className="w-7 h-7 flex items-center justify-center rounded-full bg-transparent border-0 p-0 leading-none text-2xl text-white/70 hover:text-white cursor-pointer">×</button>
                                </div>
                                <p className="mb-0! w-full">There is only {availableChanges} change and you can't change more than once, so please check before proceeding.</p>
                            </div>
                        )
                    })()}
                </div>

                {(() => {
                    const cancelPolicy = e.getCancelPolicy()
                    return (
                        <CancelRide trip={e.trip} disabled={!cancelPolicy.canCancel} blockedReason={cancelPolicy.blockedReason} tooltipAlign="center"
                                    className="cursor-pointer w-full py-[15px] bg-transparent border-0 text-base text-(--text-primary) text-center disabled:opacity-50 disabled:cursor-not-allowed">
                            {isReturnFlow ? 'Cancel Return Booking' : 'Cancel Booking'}
                        </CancelRide>
                    )
                })()}

                <p className="mb-0 text-sm text-center cursor-pointer underline" onClick={() => setShowPoliciesPopup(true)}>By proceeding, you confirm that you understand and agree to these terms.</p>
            </div>

            {/* Smart Time Popup */}
            {/* flushBody/flushTop because the screens bring their own spacing: their
                `.modalPages-spacing` gutters, and their own ModalHeader as the first
                element — the Modal's px-8 and headerless top padding sat on top of both. */}
            <Modal isOpen={e.smartTimePopup} onClose={e.closeSmartTime} className="md:max-w-157.5" flushBody flushTop>
                {/* The booking flow's recommended-pickup screens (chooser -> recommendation).
                    They render their own headers, so no ModalHeader here — the outer
                    "Smart Time Select" title belonged to the DateTimeSection this replaced. */}
                <SmartPickupFlow
                    destinations={[
                        e.committedDestinations?.origin,
                        e.committedDestinations?.destination,
                        ...(e.committedDestinations?.stopOvers || []),
                    ].filter(Boolean)}
                    rideDate={e.liveDatetime.date}
                    routeData={e.trip?.route}
                    flightNumber={e.sharedFlightNumber}
                    flightDetails={e.sharedFlightDetails}
                    onFlightChange={({flightNumber, flightDetails}) => {
                        e.setSharedFlightNumber(flightNumber);
                        e.setSharedFlightDetails(flightDetails);
                    }}
                    onSelectTime={e.handleSmartTimeSelect}
                    onClose={e.closeSmartTime}
                />
            </Modal>

            {/* Flight-number prompt after confirm (isToAirport only) */}
            <Modal isOpen={e.showFlightPrompt} onClose={() => {}} className="md:max-w-137.5" footer={
                <>
                    <button type="button"
                            disabled={!e.sharedFlightDetails || e.finalizeFlightMutation.isPending}
                            onClick={() => e.finalizeFlightMutation.mutate()}
                            className="hopn-add-passenger-button">
                        Complete your booking
                    </button>
                    {e.flightFinalizeError && <p className="text-red-500 text-center text-sm mt-2 mb-0!">{e.flightFinalizeError}</p>}
                </>
            }>
                <DateTimeSection
                    destinations={e.committedDestinations} routeData={e.trip?.route} rideDate={e.liveDatetime.date}
                    flightNumber={e.sharedFlightNumber} setFlightNumber={e.setSharedFlightNumber}
                    flightDetails={e.sharedFlightDetails} setFlightDetails={e.setSharedFlightDetails}
                    flightLoading={e.sharedFlightLoading} setFlightLoading={e.setSharedFlightLoading}
                    flightError={e.sharedFlightError} setFlightError={e.setSharedFlightError}
                    setFlightUnavailable={e.setSharedFlightUnavailable}
                    timeApplied={e.sharedTimeApplied} setTimeApplied={e.setSharedTimeApplied}
                    onSelectTime={e.handleSmartTimeSelect}
                    hideManualOption={true}
                    hideTimeSuggestion={true}
                    dateTimeSectionRef={e.dateTimeSectionRef}
                />
            </Modal>

            {/* Section Edit Modal */}
            <Modal isOpen={!!e.editingSection} onClose={e.closeEdit}>
                <ModalHeader
                    title={
                        e.passengerView === 'seatDetails' && e.editingSection === 'passengers' ? 'Found Right Seat'
                        : e.luggageOversizedMode && e.editingSection === 'luggage' ? 'Add Oversized'
                        : e.carBreakdownId && e.editingSection === 'car' ? 'Payment'
                        : e.editingSection === 'destination' ? ((e.destinationPart === 'origin' || e.destinationPart === 'originStops') ? (e.isFromAirport ? 'From Airport' : 'Start Ride Address') : (e.destinationPart === 'dropoff' || e.destinationPart === 'destinationOnly') ? 'Drop off Address' : 'Pick-up & Drop-off')
                        : e.editingSection === 'car' ? 'Select Vehicle'
                        : e.editingSection === 'passengers' ? (e.passengerOnly === 'childseats' ? 'Child Seats' : e.passengerOnly === 'passengers' ? 'Passengers' : 'Passengers & Child Seats')
                        : e.editingSection === 'luggage' ? 'Luggage'
                        : ''
                    }
                    onBack={
                        (e.passengerView === 'seatDetails' && e.editingSection === 'passengers') ? () => e.setPassengerView('main')
                        : (e.carBreakdownId && e.editingSection === 'car') ? () => e.setCarBreakdownId(null)
                        : undefined
                    }
                    onClose={
                        (e.carBreakdownId && e.editingSection === 'car')
                            ? undefined : e.closeEdit
                    }
                />
                {renderSection()}
            </Modal>

            <SpecialRequestPopup isOpen={e.showSRPopup} onClose={() => e.setShowSRPopup(false)} specialRequest={e.specialRequest} onSave={(val) => { e.setSpecialRequest(val); e.setShowSRPopup(false) }} notesPolicy={e.getFieldPolicy('special_request')} petPolicy={e.getFieldPolicy('having_pet')} quietRidePolicy={e.getFieldPolicy('quiet_ride')}/>
            <FlightDelayedPopup isOpen={e.infoPopup === 'flightDelayed'} onClose={() => e.setInfoPopup(null)}/>
            <LostBaggagePopup isOpen={e.infoPopup === 'lostBaggage'} onClose={() => e.setInfoPopup(null)}/>
            <AirportPickupPerksPopup isOpen={e.infoPopup === 'morePerks'} onClose={() => e.setInfoPopup(null)}/>
            {/* Cancellation, Change & Refund — one merged CMR popup (opens on the
                Cancellation tab; Refund is a tab within it). The old separate 'refund'
                popup was removed with its pill. */}
            <PoliciesPopup isOpen={e.infoPopup === 'cancellation'} onClose={() => e.setInfoPopup(null)} trip={e.trip} initialStep="cancellationPolicies"/>
            <PayAfterDropoffPopup isOpen={e.infoPopup === 'payAfterDropoff'} onClose={() => e.setInfoPopup(null)}/>
            <ComingSoonPopup isOpen={e.infoPopup === 'findDriver'} onClose={() => e.setInfoPopup(null)} title="Find Your Driver"/>
            <SuccessPopup isOpen={e.showSuccess} isReturnFlow={isReturnFlow} isEdit={true} onDone={() => navigate('/')}/>
            <PoliciesPopup isOpen={showPoliciesPopup} onClose={() => setShowPoliciesPopup(false)} trip={e.trip}/>

            {/* Exclusive Airport Pickup Perks — perks list + nested perk detail views */}
            <AirportPerksModal isOpen={showPerksModal} onClose={() => setShowPerksModal(false)} trip={e.trip}/>
        </div>
    )
}
